import { cn } from './lib/cn';
import { Avatar, type AvatarProps } from './Avatar';

export interface AvatarStackItem {
  id: string;
  name: string;
  src?: string;
}

export interface AvatarStackProps {
  people: readonly AvatarStackItem[];
  /** How many avatars to show before collapsing into a "+N" chip. */
  max?: number;
  size?: AvatarProps['size'];
  /** Overlap amount. `tight` = -8px (reply facepiles), `loose` = -6px. */
  overlap?: 'tight' | 'loose' | 'none';
  /** Show the "+N" chip when people.length exceeds `max`. */
  showOverflow?: boolean;
  /** Accessible summary, e.g. "5 people replied". */
  label?: string;
  className?: string;
}

const OVERLAP_CLASS = {
  tight: '-space-x-2', // -8px, matches the reference facepiles
  loose: '-space-x-1.5',
  none: 'gap-0.5', //  side by side: tiny initials stay legible
} as const;

const CHIP_SIZE: Record<string, string> = {
  xs: 'h-5 min-w-5 text-3xs',
  sm: 'h-6 min-w-6 text-3xs',
  md: 'h-8 min-w-8 text-2xs',
  lg: 'h-10 min-w-10 text-xs',
};

/** Overlapping facepile with a "+N" overflow chip — replies, participants, people online. */
export function AvatarStack({
  people, max = 3, size = 'md', overlap = 'tight',
  showOverflow = true, label, className,
}: AvatarStackProps) {
  const visible = people.slice(0, max);
  const overflow = people.length - visible.length;

  return (
    <span
      className={cn('inline-flex items-center', OVERLAP_CLASS[overlap], className)}
      role="group"
      aria-label={label ?? `${people.length} people`}
    >
      {visible.map((person) => (
        <Avatar key={person.id} name={person.name} src={person.src} size={size} ring />
      ))}

      {showOverflow && overflow > 0 ? (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-pill bg-secondary px-1.5 font-semibold text-secondary-foreground ring-2 ring-card',
            CHIP_SIZE[size ?? 'md'] ?? CHIP_SIZE.md,
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  );
}
